import type { ChainId_L1 } from '@cronos-app/sdk';
import { ChainId, ChainLayer } from '@cronos-app/sdk';
import { getSDKs } from '@ui/utils';

import type { TokenConfig } from './model';
import { Token } from './model';
import { getAgentFunToken } from './registers/agentfun';
import { getAmplyToken } from './registers/amply';
import { getCorgiAIToken } from './registers/corgiai';
import { getCROToken } from './registers/cro';
import { getDaiToken } from './registers/dai';
import { getEthToken } from './registers/eth';
import { getFULToken } from './registers/ful';
import { getH2Token } from './registers/h2';
import { getLcroToken } from './registers/lcro';
import { getMERYToken } from './registers/mery';
import { getMOONToken } from './registers/moon';
import { getUSDCToken } from './registers/usdc';
import { getUSDTToken } from './registers/usdt';
import { getVEthToken } from './registers/veth';
import { getVUsdToken } from './registers/vusd';
import { getWBTCToken } from './registers/wbtc';
import { getWzkCroToken } from './registers/wzkcro';
import { getYbETHToken } from './registers/ybeth';
import { getYbUSDToken } from './registers/ybusd';
import { getZkCroToken } from './registers/zkcro';

export const TOKEN_LIST: Exclude<Token, Token.Unknown>[] = [
  Token.CorgiAI,
  Token.CRO,
  Token.DAI,
  Token.FUL,
  Token.USDC,
  Token.USDT,
  Token.LCRO,
  Token.ETH,
  Token.WBTC,
  Token.zkCRO,
  Token.vETH,
  Token.vUSD,
];

const TOKEN_LIST_L1: Exclude<Token, Token.Unknown>[] = [
  Token.ETH,
  Token.CRO,
  Token.WBTC,
  Token.USDC,
  Token.USDT,
  Token.DAI,
  Token.FUL,
  Token.zkCRO,
];

const TOKEN_LIST_L2: Exclude<Token, Token.Unknown>[] = [
  Token.vETH,
  Token.zkCRO,
  Token.WBTC,
  Token.vUSD,
  Token.FUL,
  Token.ETH,
  Token.CRO,
  Token.USDC,
];

const sdksByNetwork: Record<ChainId_L1, ReturnType<typeof getSDKs>> = {
  [ChainId.ETHEREUM]: getSDKs(ChainId.ETHEREUM),
  [ChainId.SEPOLIA]: getSDKs(ChainId.SEPOLIA),
};

const getConfigByNetwork: (
  desiredNetwork: ChainId_L1,
) => Record<Token, TokenConfig> = (
  desiredNetwork,
): Record<Token, TokenConfig> => {
  const sdks = sdksByNetwork[desiredNetwork];
  return {
    // placeholder
    [Token.Unknown]: {
      type: Token.Unknown,
      symbol: 'Unknown',
      addressL1: '',
      addressL2: '',
      addressCronosEvm: '',
      decimals: 18,
      displayDecimals: 18,
      Icon: () => null,
    },
    [Token.CRO]: getCROToken(desiredNetwork, sdks),
    [Token.CorgiAI]: getCorgiAIToken(desiredNetwork, sdks),
    [Token.DAI]: getDaiToken(desiredNetwork, sdks),
    [Token.ETH]: getEthToken(desiredNetwork, sdks),
    [Token.FUL]: getFULToken(desiredNetwork, sdks),
    [Token.LCRO]: getLcroToken(desiredNetwork, sdks),
    [Token.USDC]: getUSDCToken(desiredNetwork, sdks),
    [Token.USDT]: getUSDTToken(desiredNetwork, sdks),
    [Token.vETH]: getVEthToken(desiredNetwork, sdks),
    [Token.vUSD]: getVUsdToken(desiredNetwork, sdks),
    [Token.WBTC]: getWBTCToken(desiredNetwork, sdks),
    [Token.zkCRO]: getZkCroToken(desiredNetwork, sdks),
    [Token.ybETH]: getYbETHToken(desiredNetwork, sdks),
    [Token.ybUSD]: getYbUSDToken(desiredNetwork, sdks),
    [Token.wzkCRO]: getWzkCroToken(desiredNetwork, sdks),
    [Token.H2]: getH2Token(desiredNetwork, sdks),
    [Token.MERY]: getMERYToken(desiredNetwork, sdks),
    [Token.MOON]: getMOONToken(desiredNetwork, sdks),
    [Token.AMPLY]: getAmplyToken(desiredNetwork, sdks),
    [Token.AGENTFUN]: getAgentFunToken(desiredNetwork, sdks),
  };
};

export const TOKEN_CONFIG_MAP: Record<
  ChainId_L1,
  Record<Token, TokenConfig>
> = {
  [ChainId.ETHEREUM]: getConfigByNetwork(ChainId.ETHEREUM),
  [ChainId.SEPOLIA]: getConfigByNetwork(ChainId.SEPOLIA),
};

const getTokenListByNetwork = (
  desiredNetwork: ChainId_L1,
): Record<ChainLayer, Exclude<Token, Token.Unknown>[]> => {
  return {
    [ChainLayer.L1]: TOKEN_LIST_L1.filter(
      (type) => !!TOKEN_CONFIG_MAP[desiredNetwork][type].addressL1,
    ),
    [ChainLayer.L2]: TOKEN_LIST_L2.filter(
      (type) => !!TOKEN_CONFIG_MAP[desiredNetwork][type].addressL2,
    ),
    [ChainLayer.CronosEvm]: TOKEN_LIST.filter(
      (type) => !!TOKEN_CONFIG_MAP[desiredNetwork][type].addressCronosEvm,
    ),
  };
};

export const TOKEN_LIST_BY_LAYER: Record<
  ChainId_L1,
  Record<ChainLayer, Exclude<Token, Token.Unknown>[]>
> = {
  [ChainId.ETHEREUM]: getTokenListByNetwork(ChainId.ETHEREUM),
  [ChainId.SEPOLIA]: getTokenListByNetwork(ChainId.SEPOLIA),
};
